import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface UrgencyBucketSectionProps {
  bucket: 'fresh' | 'warming' | 'critical'
  count: number
  children: ReactNode
}

/**
 * Bucket section header for the SOLY-SALES-001 Discussion commerciale view
 * when filter = quote_issued. Renders a colored dot + libellé FR + count, then
 * the list of client cards. Hidden by the parent when count == 0.
 *
 * Color tokens reuse the BusinessDashboardPage:PendingQuotesAgingCard palette
 * (single source of truth for the bucket colors patron ↔ vendeur).
 */
export function UrgencyBucketSection({ bucket, count, children }: UrgencyBucketSectionProps) {
  const config = BUCKET_CONFIG[bucket]

  return (
    <section
      className="space-y-3"
      data-testid={`urgency-bucket-section-${bucket}`}
      data-bucket-count={count}
    >
      <header className="flex items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2">
          <span
            className={cn('inline-block h-2.5 w-2.5 rounded-full', config.dotClass)}
            aria-hidden="true"
          />
          <h3 className="text-sm font-medium">{config.label}</h3>
          <span className="text-xs text-muted-foreground">{config.sublabel}</span>
        </div>
        <span className="text-xs font-medium text-muted-foreground">{count}</span>
      </header>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  )
}

const BUCKET_CONFIG: Record<
  'fresh' | 'warming' | 'critical',
  { label: string; sublabel: string; dotClass: string }
> = {
  fresh: {
    label: 'Frais',
    sublabel: '< 15 jours',
    dotClass: 'bg-emerald-500',
  },
  warming: {
    label: 'À relancer',
    sublabel: '15–30 jours',
    dotClass: 'bg-amber-500',
  },
  critical: {
    label: 'Critique',
    sublabel: '> 30 jours',
    dotClass: 'bg-red-500',
  },
}
